import { Card, CardBody, CardHeader, Col, Form, Row } from "reactstrap";
import { LabelText } from "../Utils/Input";
import formSchema from "../Utils/schema.json";
import { Formik } from "formik";
import { validationSchemaForUpdateSimulate } from "./constants/validationSchema";
import { RenderField } from "../Utils/renderFields";
import { RenderErrorMessage } from "../Utils/renderErrorMessages";
import CommonButton from "../Utils/CommonButton";
import { useLocation, useNavigate } from "react-router-dom";
import { customerIdData } from "./constants/constantFunctionsView";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Loader } from "../Utils/Loader";
import { optionsForSubscriptionId, uomOptions } from "./constants/SessionFunctions";
import { fetchEventTypes } from "../../slices/sessions/eventType/thunk";
import { fetchDetailedBalance } from "../../slices/customer/detailedBalance/thunk";
import SelectField from '../Utils/SelectField';
import { getAllAttribute } from "../../slices/services/addservice/provisionalattribute/thunk";
import { updateTerminateSessionApiCall } from "./constants/UpdateTerminatejson";

export const UpdateSimulate = ({
    updateSession,
    setUpdateSession,
    sessionInfo,
    terminateSessions,
    setTerminateSessions,
    setButtonInitiateSession,
    onClose 
}) => {
    const navigate = useNavigate();
    const customerId = customerIdData();
    const dispatch = useDispatch();
    const location = useLocation();
    const [modalForSimulate, setModalForSimulate] = useState(false);
    const [loading, setLoading] = useState(false);
    const [eventTypeData, setEventTypeData] = useState();
    const [uomData, setUomData] = useState();

    let initialValues = {
        sessionId: '',
        sequence: '',
        sessionTime: null,
        requestedUnits: '',
        usedUnits: '',
        attributes: [{ name: '', value: '' }]
    };

    initialValues.sessionId = sessionInfo.sessionId
    initialValues.sequence = sessionInfo.sequenceNumber
    initialValues.attributes = (sessionInfo.attribute || []).map(attr => ({
        name: { value: attr.attributeName, label: attr.attributeName },
        value: attr.attributeValue
    }));
    const isView = location.pathname.includes('view-customer')

    useEffect(() => {
        if (customerId) {
            dispatch(fetchDetailedBalance(customerId, setLoading, false, navigate, isView, setModalForSimulate,));
        }
    }, [customerId]);

    useEffect(() => {
        dispatch(getAllAttribute(setLoading));
    }, [dispatch]);

    let detailedBalanceData = useSelector((state) => state.DetailedBalance.detailedBalance);
    let eventType = useSelector((state) => state.EventType.eventType);
    const attributes = useSelector((state) => state.Provisional.provisional);
    const filteredAttributes = attributes.filter(attr =>
        (attr.type === "customer" || attr.type === "usage") &&
        attr.valueType !== "list"
    );
    const attributeNameOptions = filteredAttributes.map(attr => ({
        value: attr.name,
        label: attr.name
    }));
    const optionsOfSubscriptions = optionsForSubscriptionId(detailedBalanceData?.resources);
    const simulateOptions = [
        {
            name: "subscriptionId",
            value: "UpdateSimulate",
            options: optionsOfSubscriptions,
        },
        eventTypeData,
        uomData,
    ];

    const addAttribute = (setFieldValue, values) => {
        const currentAttributes = values.attributes || [];
        const hasInvalidAttributes = currentAttributes.some(attr => !attr.name || !attr.value);

        if (hasInvalidAttributes) {
            return;
        }

        const newAttributes = [...values.attributes, { name: '', value: '' }];
        setFieldValue('attributes', newAttributes);
    };

    const removeAttribute = (index, setFieldValue, values) => {
        const newAttributes = values.attributes.filter((_, i) => i !== index);
        setFieldValue('attributes', newAttributes);
    };

    const handleAttributeChange = (index, field, value, setFieldValue, values) => {
        const newAttributes = [...values.attributes];

        if (field === 'name') {
            const selectedAttribute = filteredAttributes.find(attr => attr.name === value.value);
            newAttributes[index][field] = value;

            if (selectedAttribute && selectedAttribute.default) {
                newAttributes[index]['value'] = selectedAttribute.default;
            } else {
                newAttributes[index]['value'] = '';
            }
        } else {
            newAttributes[index][field] = value;
        }

        setFieldValue('attributes', newAttributes);
    };

    const validateAttributes = (attributes) => {
        if (!attributes || attributes.length === 0) return true;

        return attributes.every(attr => attr.name && attr.value);
    };

    const handleCancel = () => {
        setUpdateSession(false);
        setTerminateSessions(false);
        setButtonInitiateSession(true);
        if (onClose) {
            onClose();
        }
    };

    const handleSubmit = (values) => {
        const originalUpdateSessionApiCall = updateTerminateSessionApiCall;
        const customCallback = () => {
            setUpdateSession(false);
            setTerminateSessions(false);
            setButtonInitiateSession(true);
            if (onClose) {
                onClose(); 
            }
        };

        originalUpdateSessionApiCall(
            values,
            dispatch,
            setLoading,
            customerId,
            customCallback, 
            isView,
            sessionInfo,
            terminateSessions,
            setButtonInitiateSession
        );
    };

    // Helper function to get field helper text (placeholder text)
    const getFieldHelperText = (field) => {
        // You can customize this based on your field requirements
        switch(field.name) {
            case 'sessionId':
                return 'Enter session ID';
            case 'sequence':
                return 'Enter sequence number';
            case 'sessionTime':
                return 'Select session time';
            case 'requestedUnits':
                return 'Enter requested units';
            case 'usedUnits':
                return 'Enter used units';
            case 'subscriptionId':
                return 'Select subscription';
            case 'usageEventType':
                return 'Select event type';
            case 'uom':
                return 'Select unit of measurement';
            default:
                return `Enter ${field.label?.toLowerCase() || field.name}`;
        }
    };

    // Helper function to get helper text class
    const getHelperTextClass = (field, touched, errors) => {
        const fieldName = field.name;
        const baseClass = "d-block ms-0 rv-sub-label";
        if (touched[fieldName] && errors[fieldName]) {
            return `${baseClass} rv-label-danger`;
        }
        return `${baseClass} rv-label-muted`;
    };

    return (
        <>
            {updateSession && (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchemaForUpdateSimulate(terminateSessions)}
                    onSubmit={handleSubmit}
                >
                    {({
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        errors,
                        touched,
                        values,
                        setFieldValue,
                        setFieldTouched,
                    }) => {
                        useEffect(() => {
                            if (values?.subscriptionId?.value) {
                                dispatch(fetchEventTypes(values?.subscriptionId?.value, setLoading, setEventTypeData));
                            }
                            setFieldValue("usageEventType", "");
                        }, [values?.subscriptionId?.value]);
                        useEffect(() => {
                            if (values?.usageEventType?.value) {
                                let UOMOptions = uomOptions(eventType, values?.usageEventType?.value);
                                const extraObject = {
                                    name: "uom",
                                    value: "UpdateSimulate",
                                    options: UOMOptions,
                                };
                                setUomData(extraObject);
                            }
                            setFieldValue("uom", "");
                        }, [values?.usageEventType?.value]);
                        return (
                            <Form id="csms" className="needs-validation" onSubmit={handleSubmit}>
                                <div>
                                    {loading && <Loader />}
                                    {formSchema.map((section, sectionIndex) =>
                                        section.title === "UpdateSimulate" &&
                                        section.sections &&
                                        section.sections.map((subSection, subSectionIndex) =>
                                            subSection?.sectionTitle ? (
                                                <div key={subSectionIndex} className=" px-2 py-2 my-2 bg-white">
                                                    <CardBody>
                                                        <Row key={sectionIndex}>
                                                            <Col lg={12}>
                                                                <Row className="gy-4">
                                                                    {subSection.fields
                                                                        .filter((field) => {
                                                                            if (terminateSessions && (field.name == "sequence" || field.name == "requestedUnits")) {
                                                                                return false;
                                                                            }
                                                                            return true;
                                                                        })
                                                                        .map((field, fieldIndex) => (
                                                                            <Col
                                                                                md={6}
                                                                                lg={6}
                                                                                key={`${subSectionIndex}-one-${fieldIndex}`}
                                                                            >
                                                                                <LabelText
                                                                                    htmlFor={field.name}
                                                                                    className={field.required ? "text-danger" : ""}
                                                                                    displayText={field.label}
                                                                                    important={field.required ? "*" : ""}
                                                                                />
                                                                                {/* Placeholder/Helper Text */}
                                                                                <LabelText
                                                                                    htmlFor={field.name}
                                                                                    classLabelName={getHelperTextClass(field, touched, errors)}
                                                                                    displayText={touched[field.name] && errors[field.name] ? errors[field.name] : getFieldHelperText(field)}
                                                                                />
                                                                                <RenderField
                                                                                    field={field}
                                                                                    handleChange={handleChange}
                                                                                    handleBlur={handleBlur}
                                                                                    values={values}
                                                                                    touched={touched}
                                                                                    errors={errors}
                                                                                    setFieldTouched={setFieldTouched}
                                                                                    setFieldValue={setFieldValue}
                                                                                    options={simulateOptions}
                                                                                />
                                                                             
                                                                            </Col>
                                                                        ))}
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                        {values?.attributes?.map((attribute, attributeIndex) => (
                                                            <Row className="mt-4" key={attributeIndex}>
                                                                <Col lg={12}>
                                                                    <div className="border-bottom mb-3 ">
                                                                        <h4 className="card-title mb-3">Attributes</h4>
                                                                    </div>
                                                                    <Row className="gy-4 mb-3 align-items-end">
                                                                        <Col xxl={3} md={3} lg={3}>
                                                                            <LabelText
                                                                                htmlFor={`attributes[${attributeIndex}].name`}
                                                                                displayText="Attribute Name"
                                                                                className="text-danger"
                                                                            />
                                                                            {/* Placeholder for Attribute Name */}
                                                                            <LabelText
                                                                                htmlFor={`attributes[${attributeIndex}].name`}
                                                                                classLabelName="d-block ms-0 rv-sub-label rv-label-muted"
                                                                                displayText="Select an attribute name from the dropdown"
                                                                            />
                                                                            <SelectField
                                                                                name={`attributes[${attributeIndex}].name`}
                                                                                options={attributeNameOptions}
                                                                                handleChange={(selectedOption) =>
                                                                                    handleAttributeChange(attributeIndex, 'name', selectedOption, setFieldValue, values)
                                                                                }
                                                                                value={attribute.name || ''}
                                                                                placeholder="Select Attribute Name"
                                                                            />
                                                                            {touched.attributes?.[attributeIndex]?.name && errors.attributes?.[attributeIndex]?.name && (
                                                                                <div className="text-danger">
                                                                                    {errors.attributes[attributeIndex].name}
                                                                                </div>
                                                                            )}
                                                                        </Col>
                                                                        <Col xxl={3} md={3} lg={3}>
                                                                            <LabelText
                                                                                htmlFor={`attributes[${attributeIndex}].value`}
                                                                                displayText="Value"
                                                                                className="text-danger"
                                                                            />
                                                                            {/* Placeholder for Attribute Value */}
                                                                            <LabelText
                                                                                htmlFor={`attributes[${attributeIndex}].value`}
                                                                                classLabelName="d-block ms-0 rv-sub-label rv-label-muted"
                                                                                displayText="Enter the attribute value"
                                                                            />
                                                                            <input
                                                                                type="text"
                                                                                name={`attributes[${attributeIndex}].value`}
                                                                                id={`attributes[${attributeIndex}].value`}
                                                                                className="form-control"
                                                                                placeholder="Enter value"
                                                                                value={attribute.value || ''}
                                                                                onChange={(e) => handleAttributeChange(attributeIndex, 'value', e.target.value, setFieldValue, values)}
                                                                                onBlur={handleBlur}
                                                                            />
                                                                            {touched.attributes?.[attributeIndex]?.value && errors.attributes?.[attributeIndex]?.value && (
                                                                                <div className="text-danger">
                                                                                    {errors.attributes[attributeIndex].value}
                                                                                </div>
                                                                            )}
                                                                        </Col>
                                                                        <Col xxl={3} md={3} lg={3} className="d-flex justify-content-start">
                                                                            {attributeIndex === 0 ? (
                                                                                <i
                                                                                    className="ri-add-circle-line text-primary fs-3"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onClick={() => {
                                                                                        if (attribute.name && attribute.value) {
                                                                                            addAttribute(setFieldValue, values);
                                                                                        }
                                                                                    }}
                                                                                    title="Add Attribute"
                                                                                />
                                                                            ) : (
                                                                                <i
                                                                                    className="ri-delete-bin-5-line text-danger fs-3"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onClick={() => removeAttribute(attributeIndex, setFieldValue, values)}
                                                                                    title="Delete Attribute"
                                                                                />
                                                                            )}
                                                                        </Col>
                                                                    </Row>

                                                                </Col>
                                                            </Row>
                                                        ))}
                                                        <Row lg={6} className=" justify-content-end mb-2 ms-2">
                                                            <div className="live-preview">
                                                                <div className="d-flex justify-content-end gap-2 mt-4 ">
                                                                    <CommonButton
                                                                        outline={true}
                                                                        className={
                                                                            "btn-label rv-secondary-btn rounded-1 px-1 ps-3 py-1  w-md-auto text-nowrap"
                                                                        }
                                                                        color={"main-color"}
                                                                        type={"button"}
                                                                        buttonAction={handleCancel}
                                                                        buttonText={"Cancel"}
                                                                        icon={
                                                                            <i className="ri-close-fill label-icon align-middle fs-16 me-1" style={{ transform: "scaleX(-1)", borderLeft: "1px solid" }}>
                                                                            </i>
                                                                        }
                                                                        btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                                                                    />
                                                                    <CommonButton
                                                                        color={"secondary"}
                                                                        type={"submit"}
                                                                        buttonText={"Confirm"}
                                                                        icon={<i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-1"></i>}
                                                                        className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1  w-md-auto text-nowrap"
                                                                        btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                                                                        disabled={
                                                                            !validateAttributes(values.attributes)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Row>
                                                    </CardBody>
                                                </div>
                                            ) : null
                                        )
                                    )}
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};